/**
 * GZ#2710 — admin session expiry after a 401.
 *
 * The axios interceptor used to clear localStorage and set
 * `window.location.href = "/signin"` then keep rejecting. In-flight calendar
 * XHRs abort (status 0) and React Query still setStates, so /calendars keeps
 * rendering with no token. That re-render is what trips React 18 #426
 * (see calendarSessionGuard.ts).
 *
 * One module flag + the same storage keys the interceptor already cleared,
 * so the calendar tree can read "session is dead" on the very next render
 * without waiting for a full document navigation.
 */

export const ADMIN_SESSION_STORAGE_KEYS = [
  "accessToken",
  "userData",
  "user",
  "permission",
  "location_restriction",
] as const;

export const ADMIN_SIGNIN_PATH = "/signin";

let sessionExpired = false;
let redirectStarted = false;

export function isAdminSessionAlive(): boolean {
  if (sessionExpired) return false;
  try {
    return Boolean(localStorage.getItem("accessToken"));
  } catch {
    return false;
  }
}

export function clearAdminSessionStorage(): void {
  for (const key of ADMIN_SESSION_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* private mode / blocked storage */
    }
  }
}

export function markAdminSessionExpired(): void {
  sessionExpired = true;
  clearAdminSessionStorage();
}

function defaultRedirect(path: string): void {
  window.location.replace(path);
}

/**
 * Clear the session and send the tab to /signin. Safe to call from every
 * parallel 401 — storage is cleared once and replace() runs once.
 */
export function expireAdminSessionAndRedirect(
  redirect: (path: string) => void = defaultRedirect
): void {
  markAdminSessionExpired();
  if (redirectStarted) return;
  redirectStarted = true;
  redirect(ADMIN_SIGNIN_PATH);
}

/** Test-only: this module keeps process-wide flags. */
export function resetAdminSessionExpiredForTests(): void {
  sessionExpired = false;
  redirectStarted = false;
}

/** Keys that belong to the person, not the session. Logout keeps them. */
const KEEP_ON_LOGOUT_PREFIXES = ["whats-new:seen:"];

/**
 * Logout: clear everything except per-user preferences, so a closed
 * "What's new" pop-up does not come back on the next login.
 */
export function clearStorageOnLogout(): void {
  try {
    const keep: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && KEEP_ON_LOGOUT_PREFIXES.some((p) => key.startsWith(p))) {
        keep.push([key, localStorage.getItem(key) ?? ""]);
      }
    }
    localStorage.clear();
    keep.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch {
    /* private mode / blocked storage */
  }
}
